import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/services/api';
import { showSnackbar } from '@/stores/snackbarStore';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'form' | 'loading' | 'success' | 'error'>('form');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setStatus('loading');
    try {
      await api.resetPassword(token, password);
      setStatus('success');
      showSnackbar('Password reset successfully. You can now log in.', 'success');
    } catch (e) {
      setStatus('error');
      setError(e instanceof ApiError ? e.message : 'Failed to reset password. Please try again.');
    }
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="bg-wall-surface border border-wall-border rounded-lg p-8 w-full max-w-md text-center">
          <h1 className="text-2xl font-bold text-wall-text mb-2">Invalid reset link</h1>
          <p className="text-wall-muted mb-6">
            This password reset link is missing or invalid. Request a new one from the login page.
          </p>
          <Link
            to="/forgot-password"
            className="inline-block bg-wall-accent text-wall-bg px-6 py-2 rounded-sm font-medium hover:opacity-90"
          >
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="bg-wall-surface border border-wall-border rounded-lg p-8 w-full max-w-md text-center">
          <div className="text-4xl text-green-500 mb-4">✓</div>
          <h1 className="text-2xl font-bold text-wall-text mb-2">Password reset</h1>
          <p className="text-wall-muted mb-6">
            Your password has been updated. You can now log in with your new password.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full py-2 bg-wall-accent text-wall-bg rounded-sm font-medium hover:opacity-90"
          >
            Log in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full p-4">
      <div className="bg-wall-surface border border-wall-border rounded-lg p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-center mb-2">Set new password</h1>
        <p className="text-wall-muted text-center text-sm mb-6">
          Enter your new password below. Use at least 8 characters.
        </p>

        {error && (
          <div className="mb-4 px-4 py-2 rounded-sm bg-red-900/50 border border-red-500 text-red-200 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-wall-muted mb-1">
              New password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-wall-bg border border-wall-border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent"
              required
              minLength={8}
              autoComplete="new-password"
              autoFocus
            />
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-wall-muted mb-1"
            >
              Confirm password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 bg-wall-bg border border-wall-border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full py-2 bg-wall-accent hover:bg-opacity-80 rounded-sm font-medium transition-colors disabled:opacity-50"
          >
            {status === 'loading' ? 'Updating…' : 'Reset password'}
          </button>
        </form>

        <p className="text-center text-wall-muted mt-6 text-sm">
          <Link to="/login" className="text-wall-accent hover:underline">
            ← Back to login
          </Link>
        </p>
      </div>
    </div>
  );
}
